import { ErrorLogs } from 'module/api/integrations/salesforce/types';
import { useTranslation } from 'module/hooks/use-translation';
import { Grid } from 'module/ui/components/atoms/grid/grid';
import { SingleLineEllipsis } from 'module/ui/components/styles';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  GridOptions,
} from 'ag-grid-community';
import { Typography } from 'antd';
import { ReactComponent as BugSvg } from 'assets/svgs/empty-states/bug-duotone.svg';
import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { formatDateWithTime } from 'utils/date-formatter';
import { EmptyState } from 'components/atoms/empty-state';

const { Text } = Typography;

const PrimaryRow = styled.div``;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 95%;
  ${PrimaryRow} {
    ${SingleLineEllipsis};
  }
`;

const ListEmptyState: FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <EmptyState
      image={<BugSvg />}
      title="No error logs found"
      description="There are no error logs to display at this time."
    />
  );
};

const StyledGrid = styled(Grid)`
  .ag-row {
    border-bottom: 1px solid #f4f4f4 !important;
    border-bottom-width: 1px !important;
    border-bottom-style: solid !important;
    border-color: var(--table-border-color) !important;
  }
  .ag-row-focus {
    background: none;
  }
  .ag-row-hover {
    background: var(--ag-row-hover-color);
  }

  .ag-cell {
    user-select: text;
    padding: 2px 8px !important; /* py-[2px] px-[8px] from Figma */
    font-size: 14px !important; /* text-[14px] from Figma */
    line-height: 18px !important; /* leading-[18px] from Figma */
    font-family: 'SF Pro', sans-serif !important;
    font-weight: 400 !important;
    color: #161616 !important;
    height: 44px !important; /* Ensure cell height matches row height */
    display: flex !important;
    align-items: center !important;
  }

  .ag-react-container {
    width: 100%;
    > div {
      width: 100%;
    }

    > :not(:first-child) {
      display: none !important;
    }
  }
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
    align-items: flex-start;
    .ag-react-container {
      padding-left: var(--layout-06);
      padding-top: calc(
        calc(var(--layout-06) + var(--layout-03)) + var(--layout-02)
      );
    }
  }
`;

enum FieldKey {
  Type = 'type',
  EntityName = 'entityName',
  ErrorMessage = 'errorMessage',
  Id = 'id',
  ExternalResourceId = 'externalResourceId',
  ExternalResourceType = 'externalResourceType',
  NativeResourceId = 'nativeResourceId',
  NativeResourceType = 'nativeResourceType',
  NativeResourceName = 'nativeResourceName',
}

const getDefaultOptions: GridOptions = {
  defaultColDef: {
    resizable: true,
    sortable: false,
    editable: false,
    lockPinned: true,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
  },
  suppressContextMenu: true,
  suppressRowClickSelection: true,
  suppressCellFocus: true,
  animateRows: true,
  debounceVerticalScrollbar: true,
  suppressAnimationFrame: true,
};

interface DefaultCellRendererParams extends ICellRendererParams {
  type: string;
}

const TimestampRenderer: FC<React.PropsWithChildren<ICellRendererParams>> = ({
  data,
}) => <span>{formatDateWithTime(data.timestamp)}</span>;

const CellRenderer: FC<React.PropsWithChildren<DefaultCellRendererParams>> = ({
  data,
  type,
}) => {
  return (
    <TextWrapper>
      <Text ellipsis={{ tooltip: true }}>{data[type]}</Text>
    </TextWrapper>
  );
};

interface Props {
  records: ErrorLogs[];
}

export const ErrorLogTable: FC<React.PropsWithChildren<Props>> = ({
  records,
}) => {
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const typeText = useTranslation('settings.integrations.sf.error_logs.type');
  const entityNameText = useTranslation(
    'settings.integrations.sf.error_logs.entity_name',
  );
  const errorMessageText = useTranslation(
    'settings.integrations.sf.error_logs.error_message',
  );
  const timestampText = useTranslation(
    'settings.integrations.sf.error_logs.timestamp',
  );
  const requestIdText = useTranslation(
    'settings.integrations.sf.error_logs.request_id',
  );
  const externalResourceIdText = useTranslation(
    'settings.integrations.sf.error_logs.external_resource_id',
  );
  const externalResourceTypeText = useTranslation(
    'settings.integrations.sf.error_logs.external_resource_type',
  );
  const nativeResourceIdText = useTranslation(
    'settings.integrations.sf.error_logs.native_resource_id',
  );
  const nativeResourceTypeText = useTranslation(
    'settings.integrations.sf.error_logs.native_resource_type',
  );

  const nativeResourceNameText = useTranslation(
    'settings.integrations.sf.error_logs.native_resource_name',
  );

  const onGridReady = (event: GridReadyEvent) => {
    setGridApi(event.api);
  };

  useEffect(() => {
    if (!gridApi) {
      return;
    }

    gridApi.setGridOption(
      'rowData',
      records.map((record) => {
        let { entityName } = record;
        let entityType = '-';
        const splits = entityName.split(':');

        if (splits.length > 1) {
          entityName = splits[1].trim();
          entityType = splits[0].trim();
        }

        return {
          id: record.requestId,
          timestamp: record.logTimestamp,
          errorMessage: record.logMessage,
          entityName,
          externalResourceId: record.tags.externalResourceId ?? '-',
          externalResourceType: record.tags.externalResourceType ?? '-',
          nativeResourceId: record.tags.nativeResourceId ?? '-',
          nativeResourceType: record.tags.nativeResourceType ?? '-',
          nativeResourceName: record.tags.nativeResourceName ?? '-',
          type: entityType,
        };
      }),
    );
  }, [gridApi, records]);

  const columns = useMemo(
    (): ColDef[] => [
      {
        headerName: timestampText,
        field: 'timestamp',
        cellRenderer: 'timestampRenderer',
        width: 180,
      },
      {
        headerName: typeText,
        field: FieldKey.Type,
        width: 170,
        cellRenderer: 'cellRenderer',
        cellRendererParams: { type: FieldKey.Type },
      },
      {
        headerName: entityNameText,
        field: FieldKey.EntityName,
        width: 500,
        cellRenderer: 'cellRenderer',
        cellRendererParams: { type: FieldKey.EntityName },
      },
      {
        headerName: errorMessageText,
        field: FieldKey.ErrorMessage,
        width: 400,
        suppressSizeToFit: true,
        cellRenderer: 'cellRenderer',
        cellRendererParams: { type: FieldKey.ErrorMessage },
      },
      {
        headerName: requestIdText,
        field: FieldKey.Id,
        width: 350,
        cellRenderer: 'cellRenderer',
        cellRendererParams: { type: FieldKey.Id },
      },
      {
        headerName: externalResourceIdText,
        field: FieldKey.ExternalResourceId,
        width: 200,
        cellRenderer: 'cellRenderer',
        cellRendererParams: { type: FieldKey.ExternalResourceId },
      },
      {
        headerName: externalResourceTypeText,
        field: FieldKey.ExternalResourceType,
        width: 200,
        cellRenderer: 'cellRenderer',
        cellRendererParams: { type: FieldKey.ExternalResourceType },
      },
      {
        headerName: nativeResourceIdText,
        field: FieldKey.NativeResourceId,
        width: 150,
        cellRenderer: 'cellRenderer',
        cellRendererParams: { type: FieldKey.NativeResourceId },
      },
      {
        headerName: nativeResourceTypeText,
        field: FieldKey.NativeResourceType,
        width: 200,
        cellRenderer: 'cellRenderer',
        cellRendererParams: { type: FieldKey.NativeResourceType },
      },
      {
        headerName: nativeResourceNameText,
        field: FieldKey.NativeResourceName,
        width: 200,
        cellRenderer: 'cellRenderer',
        cellRendererParams: { type: FieldKey.NativeResourceName },
      },
    ],
    [
      entityNameText,
      errorMessageText,
      externalResourceIdText,
      externalResourceTypeText,
      nativeResourceIdText,
      nativeResourceTypeText,
      nativeResourceNameText,
      requestIdText,
      timestampText,
      typeText,
    ],
  );

  return (
    <StyledGrid
      {...getDefaultOptions}
      className="ag-theme-balham"
      onGridReady={onGridReady}
      components={{
        timestampRenderer: TimestampRenderer,
        cellRenderer: CellRenderer,
        ListEmptyState,
      }}
      noRowsOverlayComponent="ListEmptyState"
      columnDefs={columns}
    />
  );
};
